import {
  gql,
  skipToken,
  useApolloClient,
  useSuspenseQuery,
} from '@apollo/client';
import { useSelector } from 'react-redux';

import { getSessionCompanyId } from '../../selectors/session';
import { Company } from '../interfaces/entities';

const query = gql`
  query getCompanyById($id: String!) {
    getCompanyById(id: $id) {
      id
      status
      defaultFullProfileAccessBalance
      CountryCode
      createdAt
      freeSubscriptionPlanGroup {
        id
      }
      verificationInfo {
        ...companyVerificationInfoFragment
      }
      location {
        ...hierarchicalLocationFragment
        parents {
          ...hierarchicalLocationFragment
        }
      }
      isVIPPurchaseEnabled
      isMonthlyQuotaEnabled
      currentCompanyJobSlotsSubscription: companyJobSlotsSubscription {
        totalConcurrentJobSlots
      }
    }
  }
`;

type QueryOutput = {
  getCompanyById: Pick<
    Company,
    | 'id'
    | 'verificationInfo'
    | 'status'
    | 'defaultFullProfileAccessBalance'
    | 'freeSubscriptionPlanGroup'
    | 'CountryCode'
    | 'createdAt'
    | 'isVIPPurchaseEnabled'
    | 'isMonthlyQuotaEnabled'
    | 'location'
    | 'currentCompanyJobSlotsSubscription'
  >;
};

type QueryInput = {
  id: string;
};

export async function preloadCompanyById(
  companyId: string,
  apolloClient: ReturnType<typeof useApolloClient>
) {
  const { data } = await apolloClient.query<QueryOutput, QueryInput>({
    query,
    variables: { id: companyId },
  });

  return data.getCompanyById;
}

export default function useGetCompanyById() {
  const companyId: string | undefined = useSelector(getSessionCompanyId);
  const { data } = useSuspenseQuery<QueryOutput, QueryInput>(
    query,
    companyId
      ? {
          variables: { id: companyId },
        }
      : skipToken
  );

  return {
    company: data?.getCompanyById,
  } as const;
}
